import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useI18n } from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { toApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Notice } from '@/components/ui/Feedback';

/**
 * The one screen a marketer sees before anything else. It carries the brand
 * mark and nothing else — no product tour, no illustration, no "welcome back".
 * A login page that takes a second to parse is a login page that is in the way.
 */
/**
 * A password field with a show/hide toggle. The eye button is `tabIndex={-1}`
 * so keyboard users tab straight from the field to the submit button; it's for
 * a quick visual check, not a stop on the path. Used on both auth screens.
 */
function PasswordInput({
  id,
  describedBy,
  invalid,
  autoComplete,
  autoFocus,
  minLength,
  value,
  onChange,
}: {
  id: string;
  describedBy?: string;
  invalid?: boolean;
  autoComplete: string;
  autoFocus?: boolean;
  minLength?: number;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        aria-describedby={describedBy}
        invalid={invalid}
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        minLength={minLength}
        required
        value={value}
        onChange={onChange}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? t.auth.hidePassword : t.auth.showPassword}
        aria-pressed={show}
        tabIndex={-1}
        className="absolute right-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-control text-faint transition-ui duration-instant hover:text-strong"
      >
        {show ? <EyeOff aria-hidden className="h-4 w-4" /> : <Eye aria-hidden className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function Login() {
  const { t, lang, setLang } = useI18n();
  const { signIn } = useAuth();
  const { theme, toggle } = useTheme();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(login.trim(), password);
    } catch (caught) {
      const api = toApiError(caught);
      setError(api.message === '__NETWORK__' ? t.errors.network : api.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-canvas px-4">
      {/* A single, very quiet brand gesture. The application itself is a
          workbench; this is the only place it is allowed to look like a door. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{
          background:
            'radial-gradient(60rem 32rem at 50% -12rem, rgb(47 77 236 / 0.10), transparent 70%)',
        }}
      />

      <div className="absolute right-4 top-4 flex items-center gap-1">
        <button
          type="button"
          onClick={() => setLang(lang === 'uz' ? 'ru' : 'uz')}
          className="h-9 cursor-pointer rounded-control px-3 font-mono text-code uppercase text-muted transition-ui duration-instant hover:bg-raised hover:text-strong"
        >
          {lang}
        </button>
        <button
          type="button"
          onClick={toggle}
          aria-label={t.palette.toggleTheme}
          className="h-9 w-9 cursor-pointer rounded-control text-muted transition-ui duration-instant hover:bg-raised hover:text-strong"
        >
          {theme === 'dark' ? '☀' : '☾'}
        </button>
      </div>

      <div className="relative w-full max-w-[24rem]">
        <div className="mb-8 flex flex-col items-center text-center">
          {/* The real brand lockup, theme-swapped by CSS so there is no flash on
              first paint. The dark-ink version rides on the light canvas; the
              white version rides on the near-black one. */}
          <img
            src="/logos/ipost-logo-full-dark.png"
            alt="iPost"
            className="mb-6 h-16 w-auto object-contain dark:hidden"
          />
          <img
            src="/logos/ipost-logo-full-light.png"
            alt="iPost"
            className="mb-6 hidden h-16 w-auto object-contain dark:block"
          />
          <h1 className="font-display text-section text-strong">{t.auth.signInTitle}</h1>
          <p className="mt-1 text-body text-muted">{t.auth.signInSubtitle}</p>
        </div>

        <form onSubmit={submit} className="card flex flex-col gap-4 p-6">
          {error && <Notice tone="danger">{error}</Notice>}

          <Field label={t.auth.login}>
            {({ id }) => (
              <Input
                id={id}
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                required
                value={login}
                onChange={(event) => setLogin(event.target.value)}
              />
            )}
          </Field>

          <Field label={t.auth.password}>
            {({ id }) => (
              <PasswordInput
                id={id}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>

          <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-2 w-full">
            {busy ? t.auth.signingIn : t.auth.signIn}
          </Button>
        </form>

        {/*
          There is deliberately no "forgot password" link. An unreachable reset
          flow is worse than none: it fails silently and people assume the
          account is broken (§4.2 / decision C6). An admin issues a new password.
        */}
        <p className="mt-4 text-center text-ui text-faint">
          Parolni unutdingizmi? Administratordan yangi parol so‘rang.
        </p>

      </div>
    </div>
  );
}

/** Forced change before anything else loads (§4.2). */
export function ChangePassword() {
  const { t } = useI18n();
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (next !== repeat) {
      setError(t.auth.passwordsDiffer);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
    } catch (caught) {
      const api = toApiError(caught);
      setError(api.message === '__NETWORK__' ? t.errors.network : api.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-[24rem]">
        <h1 className="font-display text-title text-strong">{t.auth.changePasswordTitle}</h1>
        <p className="mt-2 text-body leading-relaxed text-muted">{t.auth.changePasswordWhy}</p>

        <form onSubmit={submit} className="card mt-6 flex flex-col gap-4 p-6">
          {error && <Notice tone="danger">{error}</Notice>}

          <Field label={t.auth.currentPassword}>
            {({ id }) => (
              <PasswordInput
                id={id}
                autoComplete="current-password"
                autoFocus
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
              />
            )}
          </Field>
          <Field label={t.auth.newPassword} hint="Kamida 10 belgi">
            {({ id, describedBy }) => (
              <PasswordInput
                id={id}
                describedBy={describedBy}
                autoComplete="new-password"
                minLength={10}
                value={next}
                onChange={(event) => setNext(event.target.value)}
              />
            )}
          </Field>
          <Field label={t.auth.repeatPassword} error={repeat && next !== repeat ? t.auth.passwordsDiffer : undefined}>
            {({ id, invalid }) => (
              <PasswordInput
                id={id}
                invalid={invalid}
                autoComplete="new-password"
                value={repeat}
                onChange={(event) => setRepeat(event.target.value)}
              />
            )}
          </Field>

          <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-2 w-full">
            {t.auth.changePassword}
          </Button>
        </form>
      </div>
    </div>
  );
}
